import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import { useCache } from 'src/lib/utils';
import { FRIEND_INACTIVITY_THRESHOLD } from 'src/replicant/gifts/gifts.ruleset';
import { duration } from 'src/replicant/utils/duration';
import StateObserver from 'src/StateObserver';
import { Feature } from 'src/replicant/analytics';
import { startInviteFriendsSequence } from '../social/social.sequence';
import { captureGenericError } from 'src/lib/sentry';
import { setUserProperties } from 'src/lib/analytics/utils';
import { getBuruFriends } from 'src/replicant/friends/friends.getters';

export async function openGiftsListPopup(
  originFeature: 'hud' | 'launch' | 'menu',
) {
  const result = await openPopupPromise('popupGiftsList', {
    origin: mapFeatureToDialogOrigin(originFeature),
    originFeature,
    additionalTracking: {
      originFeature,
    },
  });

  // Use clicked on invite button
  if (result === 'close_invite') {
    await startInviteFriendsSequence(
      Feature.INVITE._,
      Feature.INVITE.INVITE_GIFTS,
    ).catch((e) => captureGenericError('Cannot invite friends', e));
  }

  return result === 'close_invite' ? 'invite' : 'close';
}

export function getInactiveFriends() {
  return useCache(
    () =>
      StateObserver.replicant.asyncGetters.getInactiveFriends(
        FRIEND_INACTIVITY_THRESHOLD,
      ),
    'inactive_friends',
    duration({ minutes: 15 }),
  );
}

const inactiveFriendsCheckedAtKey = 'inactiveFriendsCheckedAt';
const inactiveFriendsCheckInterval = duration({ days: 1 });

/**
 * Set `lastEntryInactiveFriend14d` from a friend search, at most once per interval per device.
 * Amplitude keeps the last value in between. The search is analytics-only, so a failure is
 * dropped instead of reported: it is almost always search backend pressure.
 */
export function updateInactiveFriendsProperty() {
  if (getBuruFriends(StateObserver.getState().user).length === 0) {
    setUserProperties({ lastEntryInactiveFriend14d: 0 });
    return;
  }

  try {
    const checkedAt = Number(localStorage.getItem(inactiveFriendsCheckedAtKey));
    if (StateObserver.now() - checkedAt < inactiveFriendsCheckInterval) return;
  } catch {
    // Without storage, check every session.
  }

  getInactiveFriends()
    .get()
    .then((inactiveFriends) => {
      const countInactive14D = inactiveFriends.filter(
        (friend) =>
          friend.updatedAt < StateObserver.now() - duration({ days: 14 }),
      ).length;
      setUserProperties({
        lastEntryInactiveFriend14d: countInactive14D,
      });

      try {
        localStorage.setItem(
          inactiveFriendsCheckedAtKey,
          `${StateObserver.now()}`,
        );
      } catch {
        // Not storing it only means checking again next session.
      }
    })
    .catch((e) => console.warn('Inactive friends search failed', e));
}
